/**
 * pitcherCondition.js — what is currently true of a pitcher on the mound.
 *
 * The game computes and RENDERS this (the effects line reads `P: -1 Worn Down`,
 * `P: +1 Prime the Pump`) but never put it in the state blob. `state.pitcher`
 * carried only {id, name, year, pitch_chips}, so a seat driving off state could
 * not see that its own pitcher was degraded — the direct input to the bullpen
 * decision, which multiple reports call the most interesting choice in the game.
 *
 * That gap has produced a wrong diagnosis in report after report: a persistent
 * -1 was filed as "hidden pitcher fatigue" (it is the Worn Down card), then the
 * ticket was withdrawn entirely (an over-correction — the mechanic is real), and
 * separately Prime the Pump was filed as "never fires" because `pitcherCardMod`
 * structurally cannot contain its bonus. Every one of those was an agent
 * reasoning correctly from instruments that could not see the mechanism.
 *
 * Everything here is derived from data the client already receives:
 * `{role}_active_cards` (both sides — active cards are public), `game_stats`,
 * and `state`. No hidden information is exposed: a card is only listed once it
 * is ACTIVE, which is exactly when the UI already shows it.
 */

/** Cards whose modifier applies to whoever is pitching. */
const PITCHER_MOD_FIELD = "pitcher_modifier";

const cardsById = (allCards) => {
  const m = new Map();
  for (const c of allCards || []) if (c?.id) m.set(c.id, c);
  return m;
};

/**
 * Effects currently applying to `role`'s pitcher.
 *
 * A debuff like Worn Down is played BY the batting team and lands ON the
 * opposing pitcher, so both sides' active cards must be scanned — reading only
 * your own would miss every debuff aimed at you, which is the common case.
 */
export function pitcherEffects(game, role, allCards) {
  if (!game || !role) return [];
  const byId = cardsById(allCards);
  const opp = role === "home" ? "away" : "home";
  const out = [];
  for (const [owner, ownerRole] of [["mine", role], ["opponent", opp]]) {
    for (const entry of game[`${ownerRole}_active_cards`] || []) {
      if (!entry || entry.burned) continue;
      const card = byId.get(entry.id);
      const value = Number(card?.[PITCHER_MOD_FIELD] || 0);
      if (!value) continue;
      // A card in MY active list buffs MY pitcher; one in the opponent's list
      // with a pitcher_modifier is aimed at my pitcher.
      out.push({
        id: entry.id,
        name: card?.name || entry.id,
        value,
        appliedBy: owner,
        description: card?.description ?? null,
      });
    }
  }
  return out;
}

/** Pitches this pitcher has thrown that resolved a chip game.
 *  Taken pitches are excluded — game_stats only counts chip-resolved pitches —
 *  so this is a floor on pitch count, not the true total. Named accordingly. */
export function chipResolvedPitches(game, pitcherId) {
  if (!game || !pitcherId) return 0;
  const stats = ((game.game_stats || {}).players || {})[pitcherId] || {};
  const types = stats.pitchTypes || {};
  return Object.values(types).reduce((n, v) => n + (Number(v) || 0), 0);
}

/**
 * Full condition for one seat's pitcher.
 *
 * @param game      full game document
 * @param role      "home" | "away" — whose pitcher
 * @param pitcher   that pitcher's roster object (needs at least id)
 * @param allCards  card catalogue, for names and modifier values
 */
export function derivePitcherCondition(game, role, pitcher, allCards) {
  if (!game || !role || !pitcher?.id) return null;
  const state = game.state || {};
  const effects = pitcherEffects(game, role, allCards);
  const netModifier = effects.reduce((n, e) => n + e.value, 0);
  const entered = state[`${role}_pitcher_entered_inning`];
  return {
    id: pitcher.id,
    name: pitcher.name ?? null,
    // Every active card touching this pitcher's roll, with who applied it.
    activeEffects: effects,
    // Net of those effects. NOT the whole roll modifier: per-pitch carries and
    // chip bonuses are separate terms — read pitches[].pitcherModBreakdown for
    // the full picture of a resolved pitch.
    netModifier,
    // Convenience for the specific debuff reports keep tripping over.
    wornDown: effects.some(e => e.id === "worn_down"),
    // A pitching change clears Worn Down and resets the chip sheet, so "how long
    // has this arm been in" is the input to that decision.
    //
    // `{role}_pitcher_entered_inning` is written only by the substitution path,
    // so a STARTER never has it. Reporting null there made both fields dead for
    // any seat that never subbed — precisely the seat that most needs to know
    // how long its starter has been out there. A starter entered in the first,
    // which is the same fallback the server itself uses (`or 1`).
    enteredInning: entered == null ? 1 : Number(entered),
    inningsOnMound: Math.max(0, Number(state.inning || 1) - (entered == null ? 1 : Number(entered))),
    // True while this arm has never been relieved — makes the inferred `1` above
    // explicit rather than something a reader has to guess at.
    isStarter: entered == null,
    // NB: this is a HALF-INNING counter that flip_half_inning resets — it is not
    // per-pitcher, and it deliberately does not reset on a substitution. Named
    // `...Inning` inside a pitcher object it read as a pitcher stat, and its
    // (correct) carry-over across a change was filed as a bug. The scope is now
    // in the name.
    battersFacedThisHalf: Number(state.batters_faced_inning || 0),
    // Floor, not total: taken pitches resolve no chip game and are not counted.
    chipResolvedPitches: chipResolvedPitches(game, pitcher.id),
  };
}
